import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { showSuccessSnackbar } from 'modules/snackbar';
import { setPortal } from 'modules/portal';
import {
  dbSettingList,
  dbSettingUseYn,
  detailDbSetting,
  editDbDetail,
  addDbSetting,
  dbSettingListDelete,
} from 'lib/api/server';

import NewInput from 'components/atoms/NewInput';
import NewButton from 'components/molecules/NewButton';
import NewFilterWrap from 'components/molecules/NewFilterWrap';
import TableBtnWrap from 'components/molecules/TableBtnWrap';
import NewTableContainer from 'components/molecules/NewTable';
import Modal from 'components/organisms/NewModal';

const DbSetting = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { portal } = useSelector(({ portal }) => ({
    portal: portal,
  }));
  const handlePortalUrl = (url) => {
    let newPortal = portal.portal;
    let popIndex = newPortal.length;
    newPortal[popIndex] = { url: url, cnt: popIndex };
    dispatch(setPortal(newPortal));
  };

  const initModal = {
    open: false,
    type: null,
    params: { sysId: '', cdId: '' },
    modalTitle: '',
    confirmBtnTitle: '',
  };
  const [modal, setModal] = useState(initModal);
  const initError = { sysId: false, cdId: false };
  const [error, setError] = useState(initError);
  const [reFetch, setReFetch] = useState(false);
  const initParams = {};
  const [params, setParams] = useState(initParams);

  const getParam = (newParams) => {
    if (JSON.stringify(params) !== JSON.stringify(newParams)) {
      setParams(newParams);
    }
  };

  /**
   * 모달 값을 변경하는 함수
   * @param newParams
   * @param name
   */
  const modalGetParam = (newParams, name) => {
    let newError = { ...error };
    if (name === 'fileType') {
      if (newParams[name].length > 10) {
        handleClickSnackbar({
          message: t('fileType이 10글자를 초과하였습니다.'),
          option: { type: 'error' },
        });
      }
    }
    if (name === 'sysId') {
      if (!newParams[name]) {
        newError = { [name]: true };
      } else {
        delete newError[[name]];
      }
    } else if (name === 'cdId') {
      if (!newParams[name]) {
        newError = { [name]: true };
      } else {
        delete newError[[name]];
      }
    }
    setError(newError);
    setModal({ ...modal, params: { ...newParams } });
  };

  /**
   * modal 열어주는 함수
   */
  const handleModalOpen = (e, data) => {
    const { name } = e.currentTarget;
    let newModal = { ...initModal };
    newModal = { ...newModal, open: true, type: name };
    setError({});
    if (name === 'add') {
      newModal = {
        ...newModal,
        modalTitle: 'DB Config 등록',
        confirmBtnTitle: '등록',
      };
    } else if (name === 'edit') {
      detailDbSetting({ sysId: data['SYS_ID'], cdId: data['CD_ID'] })
        .then((response) => {
          const { data } = response.data;
          newModal = {
            ...newModal,
            modalTitle: '수정',
            confirmBtnTitle: '수정',
            params: {
              sysId: data[0]['SYS_ID'],
              cdId: data[0]['CD_ID'],
              sysDetail: data[0]['SYS_DETAIL'],
              sysDesc: data[0]['SYS_DESC'],
              fileType: data[0]['FILE_TYPE'],
              filePath: data[0]['FILE_PATH'],
            },
          };
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setModal(newModal);
        });
    } else if (name === 'delete') {
      newModal = {
        ...newModal,
        modalTitle: '삭제',
        confirmBtnTitle: '삭제',
        headerOpen: false,
        params: {
          sysDesc: data['SYS_DESC'],
          sysId: data['SYS_ID'],
          cdId: data['CD_ID'],
        },
      };
    }
    if (name !== 'edit') {
      setModal(newModal);
    }
  };

  /**
   * 모달 닫아주는 함수
   */
  const handleModalClose = () => {
    let newModal;
    newModal = { ...modal, open: false };
    setModal(newModal);
  };

  /**
   * 모달에 submit 버튼을 눌렀을 때 실행되는 함수
   */
  const handleSubmit = () => {
    let newParams = { ...modal.params };
    if (Object.keys(error).length === 0) {
      if (Object.keys(newParams).length === 0) {
        setError({ sysId: true, cdId: true });
        return;
      } else {
        if (newParams['sysId'] === '' || newParams['cdId'] === '') {
          for (const key in newParams) {
            if (newParams['sysId'] === '' && newParams['cdId'] === '') {
              setError({ sysId: true, cdId: true });
              return;
            } else if (
              (key === 'sysId' && newParams['sysId'] === '') ||
              (key === 'cdId' && newParams['cdId'] === '')
            ) {
              setError({ [key]: true });
              return;
            }
          }
        }
      }
    } else {
      for (const key in newParams) {
        if (
          (key === 'sysId' && newParams['sysId'] === '') ||
          (key === 'cdId' && newParams['cdId'] === '')
        ) {
          setError({ [key]: true });
        } else {
          return;
        }
      }
    }
    for (const key in newParams) {
      if (!newParams[key]) {
        delete newParams[key];
      }
    }
    if (modal.type === 'add') {
      addDbSetting(newParams)
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setReFetch(false);
        });
    } else if (modal.type === 'edit') {
      editDbDetail(modal.params)
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setReFetch(false);
        });
    } else if (modal.type === 'delete') {
      dbSettingListDelete({
        sysId: modal.params.sysId,
        cdId: modal.params.cdId,
      })
        .then((response) => {
          const { message } = response.data;
          handleClickSnackbar({
            message: message,
            option: { type: 'success' },
          });
          setReFetch(true);
        })
        .catch((error) => {
          handleClickSnackbar({
            message: error,
            option: { type: 'error' },
          });
        })
        .finally(() => {
          setReFetch(false);
        });
    }
    setModal({ ...modal, open: false });
  };

  const addColumnList = (() => {
    const result = new Map();
    result.set(0, {
      id: 'MANAGE_ICONS',
      label: t('관리'),
      align: 'center',
      makeRowFunc: (data) => {
        const showWindowUrl =
          '/system/server/db_detail?' +
          'sysId=' +
          data['SYS_ID'] +
          '&cdId=' +
          data['CD_ID'] +
          '&uri=/system/server/db_detail&isPop=1';
        return (
          <TableBtnWrap>
            <NewButton
              name="detail"
              title={t('상세보기')}
              iconBtn
              icon="faExternalLink"
              onClick={(e) => handlePortalUrl(showWindowUrl)}
            />
            <NewButton
              name="edit"
              title={t('수정')}
              iconBtn
              icon="faEdit"
              onClick={(e) => handleModalOpen(e, data)}
              disableRipple
            />
            <NewButton
              name="delete"
              title={t('삭제')}
              iconBtn
              icon="faTrashAlt"
              onClick={(e) => handleModalOpen(e, data)}
              disableRipple
            />
          </TableBtnWrap>
        );
      },
    });
    return result;
  })();

  const customized = {
    MANAGE_ICONS: { headStyle: { width: 70 } },
    USE_YN: {
      makeBody: (rowObj, headsMap) => {
        return (
          <NewButton
            title={t('사용여부 on/off')}
            icon="faPowerOff"
            iconBtn
            onClick={() => handleStateToggle(rowObj)}
            style={rowObj.USE_YN === '01' ? { backgroundColor: '#79bd2f' } : {}}
            color={rowObj.USE_YN === '01' ? '#fff' : undefined}
          />
        );
      },
      headStyle: { width: 100 },
    },
  };

  const handleStateToggle = (rowData) => {
    const newParams = {
      sysId: rowData['SYS_ID'],
      cdId: rowData['CD_ID'],
      useYn: rowData['USE_YN'] === '01' ? '02' : '01',
    };
    dbSettingUseYn(newParams)
      .then((response) => {
        const { message } = response.data;
        handleClickSnackbar({
          message: message,
          option: { type: 'success' },
        });
        setReFetch(true);
      })
      .catch((error) => {
        handleClickSnackbar({
          message: error,
          option: { type: 'error' },
        });
      })
      .finally(() => {
        setReFetch(false);
      });
  };

  /**
   * 스낵바 리덕스 값을 변경해주는 함수
   * @type {(...args: any[]) => any}
   */
  const handleClickSnackbar = useCallback((snackbarOptions) => {
    dispatch(showSuccessSnackbar(snackbarOptions));
    //eslint-disable-next-line
  }, []);

  return (
    <>
      <NewFilterWrap>
        <NewButton
          type="modalBtn"
          name="add"
          onClick={handleModalOpen}
          disableRipple
        >
          {t('등록')}
        </NewButton>
      </NewFilterWrap>
      <NewTableContainer
        dataReqPromise={dbSettingList}
        params={params}
        getParam={getParam}
        reFetch={reFetch}
        customized={customized}
        addColumnList={addColumnList}
        useExcel={false}
        useFetch={false}
        useTotal={false}
        useFullScreen={false}
        rowsPerPage={25}
      />
      <Modal
        open={modal.open}
        headerTitle={modal.modalTitle}
        handleClose={handleModalClose}
        confirmBtnTitle={modal.confirmBtnTitle}
        confirmOnClick={handleSubmit}
        useForm={modal.type !== 'delete'}
      >
        {(modal.type === 'add' || modal.type === 'edit') && (
          <>
            <tr>
              <th>{t('* sysId')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="sysId"
                  params={modal.params}
                  value={modal.params.sysId}
                  callBack={modalGetParam}
                  error={error.sysId}
                  disabled={modal.type === 'edit' ? true : undefined}
                />
              </td>
            </tr>
            <tr>
              <th>{t('* cdId')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="cdId"
                  params={modal.params}
                  value={modal.params.cdId}
                  callBack={modalGetParam}
                  error={error.cdId}
                  disabled={modal.type === 'edit' ? true : undefined}
                />
              </td>
            </tr>
            <tr>
              <th>{t('sysDetail')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="sysDetail"
                  params={modal.params}
                  value={modal.params.sysDetail}
                  callBack={modalGetParam}
                  error={error.sysDetail}
                />
              </td>
            </tr>
            <tr>
              <th>{t('description')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="sysDesc"
                  params={modal.params}
                  value={modal.params.sysDesc}
                  callBack={modalGetParam}
                />
              </td>
            </tr>
            <tr>
              <th>{t('fileType')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="fileType"
                  params={modal.params}
                  value={modal.params.fileType}
                  callBack={modalGetParam}
                  helperText={t('영문기준 10글자 제한')}
                />
              </td>
            </tr>
            <tr>
              <th>{t('filePath')}</th>
              <td>
                <NewInput
                  type="tableInput"
                  name="filePath"
                  params={modal.params}
                  value={modal.params.filePath}
                  callBack={modalGetParam}
                />
              </td>
            </tr>
          </>
        )}
        {modal.type === 'delete' && (
          <p>
            {modal.params.sysDesc === null
              ? '선택하신 항목을 '
              : modal.params.sysDesc + ' 을(를) '}
            삭제하시겠습니까?
          </p>
        )}
      </Modal>
    </>
  );
};
export default DbSetting;
